<template>
  <VdCard
    class="classes-list-item-component"
    expandable
    :expanded="isCardExpanded"
    size="small"
    :data-cy="`class-${clazz.technicalName}`"
  >
    <div class="class-list-item-content">
      <div class="name">
        <h3 class="displayed">
          <component :is="isCatalogScope ? 'div' : 'router-link'" :to="editClassLink" data-cy="class-display-name">
            {{ clazz.displayName }}
          </component>
        </h3>
        <h5 class="technical" data-cy="class-technical-name">{{ clazz.technicalName }}</h5>
      </div>

      <VdTooltip v-if="!isClassDefault" :content="`Cannot edit a class of type ${clazz.type}`">
        <VdIcon store="fa" color="var(--vd-base-primary-color)" name="circle-info" size="large" />
      </VdTooltip>
      <div v-if="isClassDefault" class="control">
        <InstancesFilter
          v-if="isCardExpanded && clazz.isMultiInstance"
          :instances="value.instances"
          data-cy="instances-filter"
          @change="modifyVisibleInstances"
        />

        <vd-dropdown v-if="!hideMenuActions && isCatalogScope && canWriteClassesCatalog">
          <vd-dropdown-item v-if="canWriteClassesCatalog" dangerous @click="onClassCatalogDelete">
            Delete from catalog
          </vd-dropdown-item>
        </vd-dropdown>
        <vd-dropdown
          v-else-if="
            !hideMenuActions &&
            ((isCatalogScope && canWriteClassesCatalog) ||
              (!isCatalogScope && (canWriteClasses || canWriteClassesCatalog)))
          "
        >
          <vd-dropdown-item v-if="canWriteClasses" @click="onClassEdit">Edit</vd-dropdown-item>
          <vd-dropdown-item v-if="canWriteClassesCatalog" @click="onClassCatalogExport">
            Export to catalog
          </vd-dropdown-item>
          <vd-dropdown-item v-if="canWriteClasses" dangerous @click="onClassDelete">Delete</vd-dropdown-item>
        </vd-dropdown>
      </div>
    </div>

    <template #expanded-content>
      <p v-if="clazz.description" class="description" data-cy="class-description">{{ clazz.description }}</p>
    </template>

    <template #expanded-footer>
      <el-table :data="clazz.attributes" stripe style="width: 100%" class="attributes-table single-instance">
        <el-table-column fixed prop="displayName" label="Display name" min-width="200px" show-overflow-tooltip>
          <template #default="attribute">
            <span
              class="monospace"
              :data-cy="`class-${clazz.technicalName}-attribute-${attribute.row.technicalName}-display-name`"
            >
              {{ attribute.row.displayName }}
            </span>
          </template>
        </el-table-column>

        <el-table-column fixed prop="technicalName" label="Technical name" min-width="200px">
          <template #default="attribute2">
            <span
              class="monospace"
              :data-cy="`class-${clazz.technicalName}-attribute-${attribute2.row.technicalName}-technical-name`"
            >
              {{ attribute2.row.technicalName }}
            </span>
          </template>
        </el-table-column>

        <el-table-column fixed prop="displayType" label="Type" width="100px" align="left">
          <template #default="attribute3">
            <span
              class="monospace"
              :data-cy="`class-${clazz.technicalName}-attribute-${attribute3.row.technicalName}-type`"
            >
              {{ attribute3.row.displayType }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          align="left"
          label="Default values"
          prop="defaultValue"
          min-width="200px"
          show-overflow-tooltip
        >
          <template #default="attributeInInstance">
            <div
              v-if="attributeInInstance.row.isArrayType"
              class="class-attribute-tags"
              :data-cy="`class-${clazz.technicalName}-attribute-${attributeInInstance.row.technicalName}-value`"
            >
              <span
                v-for="(item, itemIndex) in clazz.instances[0].values[attributeInInstance.row.technicalName] || []"
                :key="`${clazz.instances[0].id}-${attributeInInstance.row.technicalName}-tags-${itemIndex}`"
              >
                {{ item }}
              </span>
            </div>
            <JsonEditor
              v-else-if="attributeInInstance.row.type === 'json'"
              :model-value="clazz.instances[0].values[attributeInInstance.row.technicalName]"
              readonly
            />
            <div
              v-else
              :data-cy="`class-${clazz.technicalName}-attribute-${attributeInInstance.row.technicalName}-value`"
            >
              <template v-if="isClassDefault">
                {{ clazz.instances[0].values[attributeInInstance.row.technicalName] }}
              </template>
              <template v-else>
                <VdIcon store="fa" name="store" color="var(--vd-color-blue-default)" size="large"></VdIcon>
                {{ getShopName(clazz.instances[0].values[attributeInInstance.row.technicalName]) }}
              </template>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="clazz.instances.length > 1" class="drawer-wrapper" @click="toggleDrawer">
        <VdIcon store="fa" :name="isDrawerOpen ? 'chevron-down' : 'chevron-right'" size="large" />
        <span>Multi instance view</span>
      </div>

      <el-table
        v-if="isDrawerOpen && clazz.instances.length > 1"
        :data="clazz.instances"
        stripe
        style="width: 100%"
        class="attributes-table"
      >
        <el-table-column prop="" fixed label="Attribute name" width="220px">
          <template #default="attribute">
            <span class="instance">{{ getColumnLabel(attribute.$index + 1, clazz) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          v-for="(attribute, index) in clazz.attributes"
          :key="attribute.technicalName"
          align="left"
          prop="defaultValue"
          min-width="200px"
          show-overflow-tooltip
        >
          <template #header>
            <div>
              {{ attribute.displayName }}
            </div>
            <div class="class-attribute-technical-name">
              {{ attribute.technicalName }}
            </div>
          </template>
          <template #default="instance">
            <div
              v-if="attribute.isArrayType"
              class="class-attribute-tags"
              :data-cy="`class-${clazz.technicalName}-attribute-${attribute.technicalName}-${index}-value`"
            >
              <span
                v-for="(item, itemIndex) in instance.row.values[attribute.technicalName] || []"
                :key="`${instance.row.id}-${attribute.technicalName}-tags-${itemIndex}`"
              >
                {{ item }}
              </span>
            </div>
            <JsonEditor
              v-else-if="attribute.type === 'json'"
              :model-value="instance.row.values[attribute.technicalName]"
              readonly
            />
            <div v-else :data-cy="`class-${clazz.technicalName}-attribute-${attribute.technicalName}-${index}-value`">
              <template v-if="isClassDefault">
                {{ instance.row.values[attribute.technicalName] }}
              </template>
              <template v-else>
                <VdIcon store="fa" name="store" color="var(--vd-color-blue-default)" size="large"></VdIcon>
                {{ getShopName(instance.row.values[attribute.technicalName]) }}
              </template>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </template>
  </VdCard>
</template>

<script lang="ts">
/**
 * This component represents a game's Class displayed in a list.
 * ClassesListItem is use in ClassesListPage and ClassesCatalogImportPage
 */
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdCard, VdTooltip } from "@voodoo.io/voodoo-ui";
import { PropType, computed, defineComponent, ref, watch } from "vue";

import InstancesFilter from "@/components/InstancesFilter/InstancesFilter.vue";
import { ClassesListPageClassInfoFragment } from "@/graphql";
import usePermissions from "@/hooks/use-permissions";
import useShopsManager from "@/modules/Store/hooks/useShopsManager";
import links from "@/router/links";

export default defineComponent({
  name: "ClassesListItem",

  components: {
    InstancesFilter,
    VdCard,
    VdTooltip,
    VdIcon,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },

    versionId: {
      type: String,
      required: true,
    },

    /**
     * The actual class object
     */
    value: {
      type: Object as PropType<ClassesListPageClassInfoFragment>,
      required: true,
    },

    /**
     * If true, hide the dropdown menu actions
     */
    hideMenuActions: {
      type: Boolean,
      default: false,
    },

    /**
     * Wether the component is displayed in the classes catalog or the classes list
     */
    scope: {
      type: String,
      default: "list",
      validate: (value: string) => ["list", "catalog"].includes(value),
    },

    /**
     * If true, the card is expanded
     */
    expanded: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["delete", "export-to-catalog", "edit", "delete-from-catalog"],

  setup(props, { emit }) {
    const { shopsToDisplay } = useShopsManager(props.gameId, props.versionId);
    const { canWriteClassesCatalog, canWriteClasses } = usePermissions();

    /**
     * Handle card toggle feature
     */
    const isCardExpanded = ref(props.expanded);

    const isDrawerOpen = ref(false);

    const toggleDrawer = () => {
      isDrawerOpen.value = !isDrawerOpen.value;
    };

    watch(
      () => props.expanded,
      (watchedExpanded) => {
        isCardExpanded.value = watchedExpanded;
      }
    );

    /**
     * Modify visible instances according to filtering done by user
     */
    const visibleInstances = ref(props.value.instances);

    const modifyVisibleInstances = (selectedInstances: string[]) => {
      visibleInstances.value = props.value.instances.filter((instance) => selectedInstances.includes(instance.id));
    };

    const clazz = computed(() => ({
      ...props.value,
      instances: visibleInstances.value.map((instance) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const values: Record<string, any> = Object.fromEntries(
          instance.values.map(({ technicalName, defaultValue }) => [technicalName, defaultValue])
        );
        return {
          ...instance,
          values,
        };
      }),
    }));

    const isClassDefault = computed(() => clazz.value.type === "DEFAULT");

    /**
     * Handle dropdown commands
     */
    const onClassDelete = () => {
      emit("delete", props.value.technicalName);
    };

    const onClassCatalogExport = () => {
      emit("export-to-catalog", props.value.technicalName);
    };

    const onClassEdit = () => {
      emit("edit", props.value.technicalName);
    };

    const isCatalogScope = computed(() => props.scope === "catalog");

    const onClassCatalogDelete = () => {
      emit("delete-from-catalog", props.value.technicalName);
    };

    const getColumnLabel = (index: number, classValues: any) => {
      if (classValues.dynamicInstances) {
        return `Instance ${index}`;
      }
      if (classValues.instances.length === 1) {
        return "Default Values";
      }
      if (classValues.instances.length > 1 && index === 1) {
        return `Instance ${index} (Default values)`;
      }
      return `Instance ${index}`;
    };

    /**
     * Links
     */
    const editClassLink = {
      name: links.games.classes.preview,
      params: {
        gameId: props.gameId,
        versionId: props.versionId,
        classId: props.value.technicalName,
      },
    };

    const getShopName = (shopId: string) => {
      const selectedShop = shopsToDisplay.value?.find((shop) => shop.id === shopId);
      return selectedShop?.name ?? "N/A";
    };

    return {
      isCardExpanded,
      isClassDefault,
      visibleInstances,
      modifyVisibleInstances,
      clazz,
      editClassLink,
      onClassDelete,
      onClassCatalogExport,
      onClassEdit,
      isCatalogScope,
      onClassCatalogDelete,
      canWriteClassesCatalog,
      canWriteClasses,
      getShopName,
      getColumnLabel,
      toggleDrawer,
      isDrawerOpen,
    };
  },
});
</script>

<style lang="scss">
.classes-list-item-component {
  margin-bottom: 20px;

  .vd-card__expanded-footer {
    padding: 1rem;
    padding-top: 0;
  }

  .el-collapse-item__content {
    padding-bottom: 0;
  }

  .el-table {
    color: var(--vd-color-text-default);
    border: 1px solid var(--vd-color-text-light-7);
    border-bottom: 1px;
    margin-top: 1rem !important;

    th.el-table__cell.is-leaf {
      background: var(--vd-color-text-light-9) !important;
    }

    .el-table__body td.el-table__cell:first-child {
      background: var(--vd-color-text-light-9) !important;
      border-right: 1px solid var(--vd-color-text-light-7);
      font-weight: 700;
    }
  }

  .el-table thead {
    color: var(--vd-color-text-default);
    font-weight: 700;

    .class-attribute-technical-name {
      font-size: font("S");
      color: var(--vd-color-text-light-2);
      margin-top: -4px;
      font-weight: 500;
    }
  }

  .description {
    margin: 0;
    white-space: pre;
  }

  .attributes-table {
    &.single-instance {
      &.el-table {
        margin-top: -1rem !important;
      }

      .el-table__body td.el-table__cell:first-child {
        background: white !important;
        border-right: none;
      }
    }

    td {
      max-width: 100%;
    }

    .cell {
      word-break: break-word;
      max-width: 100%;
    }

    .class-attribute-tags {
      @extend %tags;
      margin-top: size("S");
      justify-content: flex-start;
    }
  }

  .drawer-wrapper {
    margin: 1rem 0;
    cursor: pointer;

    svg {
      margin-right: 0.5rem;
      font-size: 12px;
    }

    span {
      margin-right: 0.5rem;
      font-weight: 700;
      color: var(--vd-color-text-default);
    }
  }

  .class-list-item-content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .name {
      padding: size("M") 0;

      a {
        text-decoration: none;
      }

      .displayed {
        color: $intense-blue;
        margin-bottom: size("M");
      }

      .technical {
        @extend %technical-name;
        color: $slate;
      }
    }

    .control {
      display: flex;
      column-gap: size("ML");
      align-items: center;
    }
  }
}
</style>
